import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";
import TextSnippetOutlinedIcon from "@mui/icons-material/TextSnippetOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

export interface MatchBlock {
  block_id: number;
  content_id: number;
  block_type?: string;
  block_type_label?: string;
  title: string;
  caption: string;
  content_format?: string | null;
}

export interface MatchTopic {
  topic_id: number;
  title: string;
  matched_blocks?: MatchBlock[];
}

function MatchSnippet({ text, query }: { text: string; query: string }) {
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "ig"));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <span key={i} style={{ color: "var(--green)", fontWeight: 700 }}>{part}</span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

function MatchPickerDialog({
  open,
  topic,
  query,
  onClose,
}: {
  open: boolean;
  topic: MatchTopic | null;
  query: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [opening, setOpening] = useState<number | null>(null);

  const openScript = (block: MatchBlock) => {
    if (!topic || opening !== null) return;
    setOpening(block.block_id);
    setTimeout(() => {
      setOpening(null);
      onClose();
      navigate(`/library/topic/${topic.topic_id}`, { state: { openContent: block.content_id } });
    }, 120);
  };

  const allBlocks = topic?.matched_blocks ?? [];
  const blocks = allBlocks.slice(0, 6);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: "1px solid var(--card-border)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ p: 2.5, pb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: "rgba(99,102,241,0.1)", display: "flex", alignItems: "center", justifyContent: "center", color: "#6366f1", flexShrink: 0 }}>
            <TextSnippetOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#6366f1", textTransform: "uppercase", letterSpacing: 0.5, lineHeight: 1.3 }}>
              Match found
            </Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "var(--text)", lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {topic ? topic.title.charAt(0).toUpperCase() + topic.title.slice(1) : ""}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: "var(--muted)", alignSelf: "flex-start" }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, pt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography sx={{ fontSize: 12.5, color: "var(--muted)", mb: 0.5 }}>
          The search term was found in {allBlocks.length} {allBlocks.length === 1 ? "script" : "scripts"}. Choose one to open it directly:
        </Typography>

        {blocks.map((block) => {
          const matchedInTitle = block.title && query && block.title.toLowerCase().includes(query.toLowerCase());
          const snippet = matchedInTitle ? block.title : block.caption;
          return (
            <Paper
              key={block.block_id}
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: "var(--soft)",
                border: "1px solid var(--card-border)",
                cursor: opening === null ? "pointer" : "default",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                transition: "all 0.2s ease",
                opacity: opening !== null && opening !== block.block_id ? 0.5 : 1,
                ...(opening === null && {
                  "&:hover": {
                    borderColor: "var(--green)",
                    bgcolor: "var(--card)",
                    transform: "translateY(-1px)",
                  },
                }),
              }}
              onClick={() => openScript(block)}
            >
              <Box sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: "rgba(74,183,73,0.1)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--green)", flexShrink: 0 }}>
                <LaunchOutlinedIcon sx={{ fontSize: 16 }} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "var(--green)", textTransform: "uppercase", letterSpacing: 0.4 }}>
                    {block.block_type_label || "Script"}
                  </Typography>
                  {block.content_format && block.content_format.toLowerCase() !== "listicle" && (
                    <Box sx={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "#6366f1", bgcolor: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.2)", borderRadius: 1, px: 0.6, py: 0.15 }}>
                      {block.content_format}
                    </Box>
                  )}
                </Box>
                {snippet && (
                  <Typography sx={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <MatchSnippet text={snippet} query={query} />
                  </Typography>
                )}
              </Box>
              <ChevronRightIcon sx={{ fontSize: 18, color: "var(--muted)", flexShrink: 0 }} />
            </Paper>
          );
        })}
      </DialogContent>

      <Box sx={{ p: 2.5, pt: 0.5, display: "flex", alignItems: "center", gap: 1.5 }}>
        <Button
          startIcon={<FolderOpenOutlinedIcon sx={{ fontSize: 16 }} />}
          onClick={() => {
            if (!topic) return;
            onClose();
            navigate(`/library/topic/${topic.topic_id}`);
          }}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: 13,
            color: "#6366f1",
            bgcolor: "rgba(99,102,241,0.08)",
            borderRadius: 2,
            px: 1.75,
            py: 0.75,
            "&:hover": { bgcolor: "rgba(99,102,241,0.14)" },
          }}
        >
          Open full topic
        </Button>
        <Button
          onClick={onClose}
          sx={{
            ml: "auto",
            textTransform: "none",
            fontWeight: 600,
            fontSize: 13,
            color: "var(--muted)",
            "&:hover": { color: "var(--text)" },
          }}
        >
          Cancel
        </Button>
      </Box>
    </Dialog>
  );
}

export default MatchPickerDialog;
